import { Component, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ConsentRequestsService } from './consent-requests.service';
import Project from '../../interfaces/project';
import { MatDialog } from '@angular/material/dialog';
import { AddRequestDialogComponent } from './add-request-dialog/add-request-dialog.component';
import { ConsentRequest } from '../../interfaces/consent-request';
import { formatDate } from '@angular/common';
import { debounce, pairwise, startWith, Subscription, timer } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { ConsentRequestFilterInput } from './graphql/consent-requests';
import { PageEvent } from '@angular/material/paginator';
import { DataFormatService } from '../../services/data-format.service';
import Organization from '../../interfaces/organization';
import { Person } from '../../interfaces/person';
import ID from '../../types/id';
import { IndividualChecklistDialogComponent } from '../consent-checklists/individual-checklist-dialog/individual-checklist-dialog.component';
import { IndividualApprovalsDialogComponent } from '../consent-approvals/individual-approvals-dialog/individual-approvals-dialog.component';
import { DefaultSnackBarService } from '../../services/default-snack-bar.service';
import { ConfirmationModalComponent } from '../../components/confirmation-modal/confirmation-modal.component';
import Sponsor from "../../interfaces/sponsor";
import Participant from "../../interfaces/participant";

export enum IndicatorState {
  INITIAL = 0,
  ALERT = 1,
  ERROR = 2,
  SUCCESS = 3,
}

type RequestIndicators = {
  checklist: IndicatorState;
  approvals: IndicatorState;
};
export type RequestTimingAndUrgency = {
  is_urgent: boolean;
  urgency_comment: string | null;
  due_date: string | null;
};
type RequestTableItem = {
  id: string;
  indicators: RequestIndicators;
  past_due: IndicatorState;
  deal: Project | null;
  developer: Organization | null;
  sponsor: Sponsor | null;
  request_type: string;
  request_details: string | null;
  received: string | null;
  timing: RequestTimingAndUrgency;
  status: string | null;
  consent_manager: Person | null;
  investor_relations: Person | null;
  asset_manager: Person | null;
  disposition_associate: Person | null;
  underwriter: Person | null;
  fund_manager: Person | null;
  consentRequest: ConsentRequest;
};

@Component({
    selector: 'consent-requests',
    templateUrl: './consent-requests.component.html',
    styleUrls: ['./consent-requests.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConsentRequestsComponent {
  public dealId: string = '';
  public statusId: string | null = null;
  public requestTypeId: string | null = null;
  public underwriterId: string | null = null;
  public assetManagerId: string | null = null;
  public fundManagerId: string | null = null;
  public requestSub: Subscription;
  public filters: ConsentRequestFilterInput = {};
  public requests: RequestTableItem[] = [];
  public currentPage: number = 1;
  public total = 1;
  public perPage = 1;
  public hasMorePages: boolean = false;
  public formSubscription: Subscription;
  public filterControls = new FormGroup({
    project_id: new FormControl<Project | string | null>(null),
    consent_manager_id: new FormControl<string | null>(null),
    investor_relations_id: new FormControl<string | null>(null),
    asset_manager_id: new FormControl<string | null>(null),
    fund_manager_id: new FormControl<string | null>(null),
    underwriter_id: new FormControl<string | null>(null),
    disposition_associate_id: new FormControl<string | null>(null),
    received_date: new FormControl<string | Date | null>(null),
    consent_status_id: new FormControl<string | null>(null),
    consent_request_type_id: new FormControl<string | null>(null),
  });
  loadingSub: Subscription;

  public columns = [
    'indicators',
    'deal',
    'request_type',
    'request_details',
    'received',
    'timing',
    'status',
    'asset_manager',
    'underwriter',
    'fund_manager',
    'consent_manager',
    'investor_relations',
    'disposition_associate',
    'actions',
  ];
  @ViewChild('scroller', { read: ElementRef }) scroller: ElementRef;
  @ViewChild('container', { read: ElementRef }) container: ElementRef;
  @ViewChild('sizeBasis', { read: ElementRef }) sizeBasis: ElementRef;
  constructor(
    public consentRequestsService: ConsentRequestsService,
    public dialog: MatDialog,
    private dataFormatter: DataFormatService,
    public _snackBar: DefaultSnackBarService
  ) {}
  ngOnInit() {
    this.consentRequestsService.fetchConsentRequests();
    this.requestSub = this.consentRequestsService.consentRequests$.subscribe(
      (requests) => {
        this.parseConsentRequests(requests.data);
        if (requests?.paginatorInfo?.currentPage) {
          this.currentPage = requests.paginatorInfo.currentPage;
        }
        if (requests?.paginatorInfo?.total)
          this.total = requests?.paginatorInfo?.total ?? 1;
        if (requests?.paginatorInfo?.perPage)
          this.perPage = requests?.paginatorInfo?.perPage ?? 1;
        this.hasMorePages = !!requests?.paginatorInfo?.hasMorePages;
      }
    );
    this.formSubscription = this.filterControls.valueChanges
      .pipe(
        startWith(this.filterControls.value),
        pairwise(),
        debounce(([oldVal, newVal]) =>
          oldVal.received_date !== newVal.received_date ? timer(500) : timer(0)
        )
      )
      .subscribe(([old, data]) => {
        let input = { ...data } as unknown as ConsentRequestFilterInput;
        let hasChanged = false;
        for (let key of Object.keys(
          input
        ) as unknown[] as (keyof ConsentRequestFilterInput)[]) {
          if (input[key] === null) {
            delete input[key];
          } else if (key === 'project_id') {
            if (typeof input[key] !== 'string') {
              input[key] = (input[key] as unknown as Project).id;
            } else {
              delete input[key];
            }
          } else if (
            typeof input[key] === 'string' &&
            input[key]?.length === 0
          ) {
            input[key] = null;
          } else if (key === 'received_date') {
            let value = input[key] as any;
            input[key] =
              value instanceof Date
                ? formatDate(value, 'yyyy-MM-dd', 'en-US')
                : null;
          }
          if (this.filters[key] !== input[key]) {
            hasChanged = true;
          }
        }
        if (hasChanged) {
          this.filters = input;
          this.consentRequestsService.getPage(this.currentPage, this.filters);
        }
      });
    this.loadingSub = this.consentRequestsService.consentsLoading$.subscribe(
      (loading: boolean) => {
        if (loading) {
          this.filterControls.disable();
        } else {
          this.filterControls.enable();
        }
      }
    );
  }
  openChecklistDialog(id: ID) {
    this.dialog
      .open(IndividualChecklistDialogComponent, {
        data: { consentRequestId: id },
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
      })
      .afterClosed()
      .subscribe((data) => {
        this.refresh();
      });
  }
  public openApprovalDialog(consent_request_id: ID) {
    this.dialog
      .open(IndividualApprovalsDialogComponent, {
        data: { consent_request_id },
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
      })
      .afterClosed()
      .subscribe((data) => {
        this.refresh();
      });
  }
  scrollerOnScroll($event: Event) {
    this.container.nativeElement.scrollLeft = (
      $event.target as HTMLElement
    ).scrollLeft;
  }
  tableOnScroll($event: Event) {
    this.scroller.nativeElement.scrollLeft = (
      $event.target as HTMLElement
    ).scrollLeft;
  }
  getPage($event: PageEvent) {
    let filters = undefined;
    if (Object.keys(this.filters).length > 0) {
      filters = this.filters;
    }
    this.consentRequestsService.getPage($event.pageIndex + 1, filters);
  }
  ngAfterViewInit() {
    let resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        this.scroller.nativeElement.children[0].style = `width: ${
          (entry.target as HTMLTableElement).scrollWidth
        }px`;
      }
    });
    resizeObserver.observe(this.sizeBasis.nativeElement);
  }
  refresh() {
    let filters = undefined;
    if (Object.keys(this.filters).length > 0) {
      filters = this.filters;
    }
    this.consentRequestsService.getPage(this.currentPage, filters);
  }
  ngOnDestroy() {
    this.requestSub?.unsubscribe();
    this.formSubscription?.unsubscribe();
    this.consentRequestsService.tearDown();
    this.loadingSub?.unsubscribe();
  }
  public openDialog() {
    const dialogRef = this.dialog
      .open(AddRequestDialogComponent, {
        data: { isExisting: false },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result.saved) {
          this.refresh();
        }
      });
  }
  public openEditDialog(consentRequest: ConsentRequest) {
    const dialogRef = this.dialog
      .open(AddRequestDialogComponent, {
        data: { input: consentRequest, isExisting: true },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result.saved) {
          this.refresh();
        }
      });
  }
  public dealChanged(deal: Project | null | undefined) {
    this.filterControls.controls.project_id.setValue(deal?.id ?? null);
  }
  public deleteRequest(id: ID) {
    let dialog = this.dialog.open(ConfirmationModalComponent, {
      data: {
        modalTitle: 'Delete Request',
        actionMessage: '',
        warningMessage: 'Permanently delete this request?',
        modalInformation: [],
      },
    });
    let dialogSub: Subscription;
    dialogSub = dialog.afterClosed().subscribe((data) => {
      if (data?.clicked === 'Ok') {
        let sub: Subscription;
        sub = this.consentRequestsService
          .deleteConsentRequest(id)
          .subscribe((task) => {
            this._snackBar.open('Deleted request successfully');
            this.refresh();
            sub?.unsubscribe();
          });
      }
      dialogSub?.unsubscribe();
    });
  }

  private parseConsentRequests(requests: ConsentRequest[]) {
    this.requests = [];
    for (let request of requests) {
      let requestTableItem: RequestTableItem = {
        id: request.id,
        consentRequest: request,
        indicators: {
          checklist: request.hasPastDue
            ? request.is_urgent_flag
              ? IndicatorState.ERROR
              : IndicatorState.ALERT
            : IndicatorState.INITIAL,
          approvals: request.allApprovalsApproved
            ? IndicatorState.SUCCESS
            : request.hasDeniedApprovals
            ? IndicatorState.ERROR
            : IndicatorState.INITIAL,
        },
        deal: request.project ?? null,
        developer: request.project?.developer ?? null,
        sponsor: request.project.participants?.find((participant: Participant) => participant.role.name == 'Sponsor')?.sponsor ?? null,
        past_due: IndicatorState.INITIAL,
        status:
          this.consentRequestsService.getRequestStatus(
            request.consent_status_id
          )?.status ?? null,
        request_type:
          this.consentRequestsService.getRequestType(
            request.consent_request_type_id
          )?.request_type ?? 'Error',
        request_details: request.details,
        received: request.received_date
          ? formatDate(request.received_date, 'MM/dd/yyyy', 'en-US')
          : null,
        timing: {
          is_urgent: request.is_urgent_flag,
          due_date: request.timing_date
            ? formatDate(request.timing_date, 'MM/dd/yyyy', 'en-US')
            : null,
          urgency_comment: request.urgency_note,
        },
        asset_manager: request.assetManager ?? null,
        fund_manager: request.fundManager ?? null,
        underwriter: request.underwriter ?? null,
        consent_manager: request.consentManager ?? null,
        investor_relations: request.investorRelations ?? null,
        disposition_associate: request.dispositionAssociate ?? null,
      };
      if (request.timing_date) {
        let now = formatDate(new Date(), 'yyyy-MM-dd', 'en-US');
        if (request.timing_date < now) {
          requestTableItem.past_due = request.is_urgent_flag
            ? IndicatorState.ERROR
            : IndicatorState.ALERT;
        }
      }
      this.requests.push(requestTableItem);
    }
  }

  protected readonly IndicatorState = IndicatorState;
}
